// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { media } from '@toggl/style'
import { Input, Select, text } from '@toggl/ui'

import { ErrorRow } from './ErrorRow'
import { getFormattedHours, getFormattedMinutes } from './utils'

type Props = {|
  +time: number,
|}

export const TotalPayCalculator = ({ time }: Props) => {
  const [rounding, setRounding] = React.useState(1)
  const [weekLength, setWeekLength] = React.useState(40)
  const [payRate, setPayRate] = React.useState(20)
  const [overtimeRate, setOvertimeRate] = React.useState(1.5)
  const [focused, setFocused] = React.useState('')

  const roundedTime = Math.round(time / rounding) * rounding
  const baseTime = Math.min(roundedTime, weekLength * 60)
  const overtime = Math.max(roundedTime - baseTime, 0)
  const totalPay = (baseTime * payRate + overtime * payRate * overtimeRate) / 60

  const handleFocus = React.useCallback(
    (e: SyntheticFocusEvent<HTMLInputElement>): void => {
      e.currentTarget.select()
      setFocused(e.currentTarget.id)
    },
    []
  )

  const handleBlur = React.useCallback((): void => setFocused(''), [])

  const handleWeekLengthChange = React.useCallback(
    (e: SyntheticKeyboardEvent<HTMLInputElement>): void =>
      setWeekLength(parseFloat(e.currentTarget.value)),
    []
  )

  const handlePayRateChange = React.useCallback(
    (e: SyntheticKeyboardEvent<HTMLInputElement>): void =>
      setPayRate(parseFloat(e.currentTarget.value)),
    []
  )

  const handleOvertimeChange = React.useCallback(
    (e: SyntheticKeyboardEvent<HTMLInputElement>): void =>
      setOvertimeRate(parseFloat(e.currentTarget.value)),
    []
  )

  return (
    <div className='no-print'>
      <text.H3>How much did you earn?</text.H3>
      <Row>
        <Label>Rounding?</Label>
        <Answer>
          <Select
            id='total-pay-rounding'
            name='total-pay-rounding'
            value={String(rounding)}
            onChange={(e) => setRounding(parseInt(e.target.value))}
          >
            <option key={1} value={1}>
              No Rounding
            </option>
            {[5, 10, 15, 30].map((value) => (
              <option key={value} value={value}>
                To nearest {value} minutes
              </option>
            ))}
            <option key={60} value={60}>
              To nearest hour
            </option>
          </Select>
          <Small>
            <span>Rounded time: </span>
            {getFormattedHours(roundedTime)} hours{' '}
            {getFormattedMinutes(roundedTime)} minutes
          </Small>
        </Answer>
      </Row>
      <Row>
        <Label>Calculate overtime after:</Label>
        <Answer>
          <StyledInput
            id='total-pay-week-length'
            name='total-pay-week-length'
            type='number'
            value={String(weekLength)}
            onChange={handleWeekLengthChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <div>hours per week</div>
          <ErrorRow
            visible={isNaN(weekLength) && focused !== 'total-pay-week-length'}
          >
            <Small>This field is required</Small>
          </ErrorRow>
        </Answer>
      </Row>
      <Row>
        <Label>Base pay rate:</Label>
        <Answer prefix='$'>
          <StyledInput
            id='total-pay-hourly-rate'
            name='total-pay-hourly-rate'
            type='number'
            value={String(payRate)}
            onChange={handlePayRateChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <div>per hour</div>
          <ErrorRow
            visible={isNaN(payRate) && focused !== 'total-pay-hourly-rate'}
          >
            <Small>This field is required</Small>
          </ErrorRow>
        </Answer>
      </Row>
      <Row>
        <Label>Overtime rate:</Label>
        <Answer>
          <StyledInput
            id='total-pay-overtime-rate'
            name='total-pay-overtime-rate'
            type='number'
            value={String(overtimeRate)}
            onChange={handleOvertimeChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <div>times base rate</div>
          <ErrorRow
            visible={
              isNaN(overtimeRate) && focused !== 'total-pay-overtime-rate'
            }
          >
            <Small>This field is required</Small>
          </ErrorRow>
        </Answer>
      </Row>
      <Row>
        <text.P1>
          {`Your Total Pay: $${isNaN(totalPay) ? '' : totalPay.toFixed(2)}`}
        </text.P1>
      </Row>
    </div>
  )
}

const Row: React.ComponentType<React.ElementConfig<'div'>> = styled.div`
  margin: 30px 0;
  ${text.paragraph3};
  h4 {
    margin: 0;
  }
  ${media.mq[1]} {
    display: flex;
    align-items: center;
    gap: 30px;
  }
`

const StyledInput: React.ComponentType<React.ElementConfig<'div'>> = styled(
  Input
)`
  width: 100px;
  margin-top: 0;
  input {
    width: 100%;
  }
`

const Label: React.ComponentType<React.ElementConfig<'label'>> = styled.label`
  display: block;
  flex: 0 0 200px;
  margin-bottom: 15px;
  font-weight: bold;
  ${media.mq[1]} {
    margin-bottom: 0;
  }
`

const Answer: React.ComponentType<React.ElementConfig<'div'>> = styled.div`
  display: flex;
  column-gap: 15px;
  align-items: flex-start;
  flex-wrap: wrap;

  > * {
    line-height: 50px;
  }

  &:before {
    line-height: 50px;
    content: ${(p: { prefix: string }) =>
      p.prefix ? `'${p.prefix}'` : 'none'};

    ${media.mq[1]} {
      position: absolute;
      margin-left: -25px;
    }
  }
`

const Small: React.ComponentType<React.ElementConfig<'small'>> = styled.small`
  ${text.paragraph4};
  display: block;
  margin-top: 10px;
  opacity: 0.9;
  flex: 1 1 100%;
`
